import { memo, useEffect, useState } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  PlusCircle,
  XCircleFill,
} from "@hl/base-components/lib/assets/icons.generated";
import CustomPaginationWrapper from "@hl/base-components/lib/table/CustomPaginationWrapper";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  TransactionStateType,
  useTransactionsDispatch,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  Box,
  Button,
  Divider,
  Group,
  Modal,
  Space,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { isEmpty } from "lodash";

import {
  _CollectionType,
  SupportedMediaType,
  TokenMetadataAttribute,
  TransactionArgs,
  TransactionType,
} from "apollo/graphql.generated";
import { fileTypeFromExtension, typeFromMime } from "utils/media/file";
import { useAuth } from "~/features/auth/context";
import AddMetadataTraitsModal, {
  CollectionAttributeFormValues,
} from "~/features/collections/forms/CreateCollectionMultistepForm/AddMetadataTraitsModal";
import UpdateMetadataMediaUploader from "~/features/collections/forms/CreateCollectionMultistepForm/UpdateMetadataMediaUploader";

import { useUpdateCollectionMetadataMutation } from "../../queries.graphql.generated";

export interface EditMetadataFormValues {
  name: string;
  description: string;
  traits: CollectionAttributeFormValues[];
  mediaUrl: string;
  mediaType: SupportedMediaType;
  coverMediaUrl: string | null;
  autoAddCollectionNameAsTrait: boolean;
}

const EditEditionMetadataModal = ({
  collectionId,
  currentName,
  currentDescription,
  attributes,
  onClose,
  currentMediaUrl,
  currentAnimationUrl,
  collectionType,
}: EditEditionMetadataModalProps) => {
  const { walletAddress } = useAuth();
  const getNewTxnId = () => `txn-${Date.now()}`;
  const [error, setError] = useState<string>("");
  const [txnId, setTxnId] = useState<string>(getNewTxnId());

  const transactionDispatch = useTransactionsDispatch();
  const transactionState = useTransactionState(txnId);

  const [loading, setLoading] = useState<boolean>(false);
  const [addMetadataTraitsModalOpen, setAddMetadataTraitsModalOpen] =
    useState<boolean>(false);

  const [activePage, setPage] = useState(1);
  const [lowerBound, setLowerBound] = useState(1);
  const [upperBound, setUpperBound] = useState(1);

  const [updateCollectionMetadata, { reset: updateCollectionMetadataReset }] =
    useUpdateCollectionMetadataMutation({
      onCompleted(data) {
        if (data && walletAddress) {
          onSubmit(data.updateCollectionMetadata);
        }
      },
      onError(err) {
        logError({
          err,
          message: "failed to update a collection's metadata",
        });
        setError(err.message);
        updateCollectionMetadataReset();
      },
    });

  const form = useForm<EditMetadataFormValues>({
    initialValues: {
      name: currentName,
      description: currentDescription,
      mediaUrl: currentMediaUrl,
      mediaType: typeFromMime[fileTypeFromExtension(currentMediaUrl)],
      coverMediaUrl: currentAnimationUrl,
      autoAddCollectionNameAsTrait: false,
      traits: [],
    },
    validate: (values) => {
      return {
        name: !values.name.trim() ? "Name is required" : null,
        description: !values.description.trim()
          ? "Description is required"
          : null,
        mediaUrl: !values.mediaUrl.trim() ? "Media is required" : null,
      };
    },
  });

  const onMediaChanged = (
    media: {
      url: string;
      type: string;
    } | null
  ) => {
    if (media) {
      form.setFieldValue("mediaUrl", media.url);
      form.setFieldValue("mediaType", typeFromMime[media.type]);
    } else {
      form.setFieldValue("mediaUrl", "");
      form.setFieldValue("coverMediaUrl", null);
      form.setFieldValue("mediaType", SupportedMediaType.IMAGE);
    }
  };

  const onCoverMediaChanged = (
    media: {
      url: string;
      type: string;
    } | null
  ) => {
    if (media) {
      form.setFieldValue("coverMediaUrl", media.url);
    } else {
      form.setFieldValue("coverMediaUrl", "");
    }
  };

  useEffect(() => {
    setLoading(true);

    const metadataTraits: CollectionAttributeFormValues[] = [];
    attributes.forEach((trait: { trait_type?: string; value?: string }) => {
      if (trait.trait_type && trait.value) {
        metadataTraits.push({ name: trait.trait_type, value: trait.value });
      }
    });
    form.setFieldValue("traits", metadataTraits);
    form.setFieldValue(
      "mediaType",
      typeFromMime[fileTypeFromExtension(currentMediaUrl)]
    );

    setLoading(false);
  }, []);

  const onSubmit = async (contractData: TransactionArgs) => {
    setError("");

    if (!collectionId) {
      throw new Error("Collection without id");
    }

    try {
      if (walletAddress) {
        const txnId = getNewTxnId();

        transactionDispatch?.({
          type: "START_TRANSACTION",
          payload: {
            id: txnId,
            entityId: collectionId,
            transactionType: TransactionType.EVM_UPDATE_METADATA,
            collectionType,
            args: contractData,
            metadata: {
              collectionId,
              nftContractAddress: walletAddress,
              txType: TransactionType.EVM_UPDATE_METADATA,
            },
          },
        });
        setTxnId(txnId);
      }
    } catch (e) {
      setError("Failed to save changes.");
      logError({
        e,
        message: "[EditionEditionMetadataModal] Failed to update",
      });
    }
  };
  const startUpdate = () => {
    updateCollectionMetadata({
      variables: {
        collectionId,
        data: {
          name: form.values.name,
          description: form.values.description,
          imageUrl: !isEmpty(form.values.coverMediaUrl)
            ? form.values.coverMediaUrl
            : form.values.mediaUrl,
          animationUrl: isEmpty(form.values.coverMediaUrl)
            ? undefined
            : form.values.mediaUrl,
          attributes: form.values.traits.map((t) => {
            return { trait_type: t.name, value: t.value };
          }),
        },
      },
    });
  };

  let buttonLabel = "Save updates";
  let buttonDisabled = false;
  if (transactionState && !transactionState.error && !!transactionState.args) {
    if (transactionState.type === TransactionStateType.WaitingSignedTx) {
      buttonLabel = "Waiting for user signature...";
      buttonDisabled = true;
    } else if (transactionState.type !== TransactionStateType.Done) {
      buttonLabel = "Updating ...";
      buttonDisabled = true;
    }
  }

  useEffect(() => {
    if (transactionState?.type === TransactionStateType.Done) {
      onClose();
    }
  }, [transactionState?.type]);

  const needCoverMedia = form.values.mediaType !== SupportedMediaType.IMAGE;
  return (
    <Modal
      opened
      title={
        <Text weight={500} size="lg">
          Update metadata
        </Text>
      }
      onClose={onClose}
      size="xl"
    >
      {loading ? (
        <Box mx="auto">
          <LoadingPage marginTop={30} marginBottom={40} />
        </Box>
      ) : (
        <>
          <Stack>
            <TextInput
              placeholder="Name"
              label="Collection name"
              {...form.getInputProps("name")}
            />
            <Textarea
              placeholder="This description will appear publicly in places like marketplaces, third-party apps, and more. Use plaintext or markdown."
              label="Description"
              minRows={3}
              {...form.getInputProps("description")}
              autosize
            />
            <Stack spacing="xs">
              <Text weight={500} size="sm">
                Artwork
              </Text>

              <BoxContainer>
                <UpdateMetadataMediaUploader
                  mediaUrl={form.values.mediaUrl}
                  mediaType={form.values.mediaType}
                  onMediaChanged={onMediaChanged}
                  mediaError={(form.errors["mediaUrl"] as string) ?? ""}
                />
                {needCoverMedia && (
                  <>
                    <Divider my="sm" />

                    <UpdateMetadataMediaUploader
                      mediaUrl={form.values.coverMediaUrl ?? null}
                      mediaType={SupportedMediaType.IMAGE}
                      onMediaChanged={onCoverMediaChanged}
                      mediaError={
                        (form.errors["coverMediaUrl"] as string) ?? ""
                      }
                      isCoverImage={true}
                    />
                  </>
                )}
              </BoxContainer>
            </Stack>
            <Stack spacing={8}>
              <Text weight={500} size="sm">
                Traits
              </Text>
              <Stack spacing={0}>
                {!isEmpty(form.values.traits) && (
                  <CustomPaginationWrapper
                    activePage={activePage}
                    setPage={setPage}
                    setLowerBound={setLowerBound}
                    setUpperBound={setUpperBound}
                    maxLength={Object.keys(form.values.traits).length}
                  >
                    {(form.values.traits ?? [])
                      .slice(lowerBound - 1, upperBound)
                      .map(({ name, value }, key) => {
                        return (
                          <div key={key}>
                            <Space h="sm" />
                            <Group position="apart">
                              <Text>{name}</Text>
                              <Group>
                                <Text color={TEXT_COLOR.SECONDARY}>
                                  {value}
                                </Text>
                                <XCircleFill
                                  cursor="pointer"
                                  onClick={() => {
                                    const clearedTraits =
                                      form.values.traits.filter(
                                        (trait) => trait.name !== name
                                      );

                                    form.setFieldValue("traits", clearedTraits);
                                  }}
                                />
                              </Group>
                            </Group>
                            <Space h="sm" />
                          </div>
                        );
                      })}
                  </CustomPaginationWrapper>
                )}
                <Button
                  fullWidth
                  color={OUTLINE_COLOR}
                  size="xl"
                  onClick={() => setAddMetadataTraitsModalOpen(true)}
                >
                  <Group noWrap spacing={4}>
                    <PlusCircle />
                    Add a trait
                  </Group>
                </Button>
              </Stack>
            </Stack>
            <Space h={4} />
          </Stack>
          <Divider mx={-16} />
          <Group grow>
            <Button mt="md" color={SECONDARY_COLOR} size="xl" onClick={onClose}>
              Cancel
            </Button>

            <Button
              mt="md"
              size="xl"
              onClick={startUpdate}
              disabled={buttonDisabled}
            >
              {buttonLabel}
            </Button>
          </Group>

          {error && (
            <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
              {error}
            </Text>
          )}
          <Text size="xs" align="center" mt={8}>
            This action requires a transaction
          </Text>

          <AddMetadataTraitsModal
            isOpen={addMetadataTraitsModalOpen}
            onClose={() => setAddMetadataTraitsModalOpen(false)}
            handleSubmit={(newAttr) => {
              form.setFieldValue("traits", [
                ...form.values.traits,
                { ...newAttr },
              ]);
              // FIXME: We may need to use the "form list" feature of Mantine form
              // instead of empty object ({}) initial value to correctly set traits dirtyness.
              form.setDirty({
                traits: true,
              });
            }}
          />
        </>
      )}
    </Modal>
  );
};

export interface EditEditionMetadataModalProps {
  collectionId: string;
  collectionType: _CollectionType;
  currentName: string;
  currentDescription: string;
  seriesImageUrls?: string[];
  attributes: TokenMetadataAttribute[];
  currentMediaUrl: string;
  currentAnimationUrl: string | null;
  onClose: () => void;
  chainId: number;
}

export default memo(EditEditionMetadataModal);
